/**
 * Precios del judicial: lo que se factura por cada demanda presentada
 * (docs/precios-judicial-plan.md). Decisiones de Edgar, 20/10/2026:
 *
 *   - se factura el mes en que la demanda se PRESENTA;
 *   - el precio depende del tipo (monitorio o verbal) y de si la deuda reclamada
 *     —el saldo en ese momento— es de hasta 2.000 € o de más;
 *   - la tasa judicial depende de si es en Cataluña o en el resto de España y,
 *     desde el 22/10/2026, también del importe (hasta 2.000 € o más), con el mismo
 *     corte que el precio, y del tipo (monitorio o verbal). Va aparte y SIN IVA:
 *     es un suplido;
 *   - el success fee judicial es el `judicial_pct` de los precios de recobro
 *     (recovery-pricing.js), sobre lo cobrado en judicial.
 *
 * Lo usan el backend (la puerta) y las pantallas. Las reglas viven solo aquí.
 */

const PROCEDURE_KINDS = { MONITORIO: 'monitorio', VERBAL: 'verbal' };
const PROCEDURE_KIND_VALUES = Object.values(PROCEDURE_KINDS);
const PROCEDURE_KIND_LABELS = { monitorio: 'Monitorio', verbal: 'Verbal' };

const REGIONS = { CAT: 'cat', REST: 'rest' };
const REGION_VALUES = Object.values(REGIONS);
const REGION_LABELS = { cat: 'Cataluña', rest: 'Resto de España' };

// Hasta 2.000 € (incluidos), el precio «bajo»; de ahí para arriba, el «alto».
const AMOUNT_THRESHOLD = 2000;

// Barcelona, Girona, Lleida y Tarragona.
const CAT_ZIP_PREFIXES = ['08', '17', '25', '43'];

const BANDS = ['low', 'high'];

/** El campo de la tasa de un tipo, una región y un tramo: `court_fee_verbal_cat_high`. */
const courtFeeField = (kind, region, band) => `court_fee_${kind}_${region}_${band}`;

// Las tasas: tipo × región × tramo, ocho.
const COURT_FEE_FIELDS = PROCEDURE_KIND_VALUES.flatMap((kind) =>
  REGION_VALUES.flatMap((region) => BANDS.map((band) => courtFeeField(kind, region, band)))
);

// Las doce piezas de la tabla: el precio por tipo y tramo, y las tasas. null = no definido.
const JUDICIAL_PRICE_FIELDS = ['monitorio_low', 'monitorio_high', 'verbal_low', 'verbal_high', ...COURT_FEE_FIELDS];

// Las tasas de antes, que se leen hasta que la tabla se vuelva a guardar (y
// entonces se vacían). Valen para todo lo que no distinguían:
//   - hasta el 22/10/2026, una por región (`court_fee_cat`): para los dos tipos y
//     los dos tramos;
//   - el 22/10/2026, una por región y tramo (`court_fee_cat_low`): para los dos tipos.
const LEGACY_COURT_FEE_FIELDS = {
  cat: 'court_fee_cat',
  rest: 'court_fee_rest',
};
const LEGACY_BAND_COURT_FEE_FIELDS = REGION_VALUES.flatMap((region) => BANDS.map((band) => `court_fee_${region}_${band}`));
const ALL_LEGACY_COURT_FEE_FIELDS = [...Object.values(LEGACY_COURT_FEE_FIELDS), ...LEGACY_BAND_COURT_FEE_FIELDS];

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const isMoney = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0;

/** Cataluña o resto por el código postal del deudor; null si no se puede saber. */
function regionFromZip(zip) {
  const z = String(zip ?? '').replace(/\D/g, '');
  if (z.length !== 5) return null;
  return CAT_ZIP_PREFIXES.includes(z.slice(0, 2)) ? REGIONS.CAT : REGIONS.REST;
}

/**
 * La tabla con las ocho tasas: si falta una y hay una antigua que la cubría (la
 * de su región y tramo, o la de su región), se usa esa. Solo los campos de
 * `JUDICIAL_PRICE_FIELDS`.
 */
function normalizeJudicialPricing(judicial) {
  if (!judicial) return null;
  const out = Object.fromEntries(JUDICIAL_PRICE_FIELDS.map((f) => [f, judicial[f] ?? null]));
  PROCEDURE_KIND_VALUES.forEach((kind) =>
    REGION_VALUES.forEach((region) =>
      BANDS.forEach((band) => {
        const f = courtFeeField(kind, region, band);
        if (isMoney(out[f])) return;
        const antigua = [judicial[`court_fee_${region}_${band}`], judicial[LEGACY_COURT_FEE_FIELDS[region]]].find(isMoney);
        if (antigua !== undefined) out[f] = antigua;
      })
    )
  );
  return out;
}

/** ¿Tiene la empresa precios judiciales? Con que haya uno, la parte judicial sale en Facturación. */
function hasJudicialPricing(judicial) {
  const j = normalizeJudicialPricing(judicial);
  return Boolean(j) && JUDICIAL_PRICE_FIELDS.some((f) => isMoney(j[f]));
}

/** Los errores de la tabla judicial, en castellano. Vacío = válida. */
function validateJudicialPricing(judicial) {
  if (!judicial) return [];
  const errors = [];
  JUDICIAL_PRICE_FIELDS.forEach((f) => {
    const v = judicial[f];
    if (v !== null && v !== undefined && !isMoney(v)) errors.push(`${f}: no es un importe válido`);
  });
  return errors;
}

/**
 * Lo que se factura por una demanda.
 * @param {{kind, claimed, region, court_fee}} demand  `court_fee` = la tasa real si se ha corregido
 * @returns {{fee: number|null, court_fee: number|null, band: 'low'|'high'}}
 *   `fee: null` = sin tipo de procedimiento o sin precio para ese tipo.
 */
function demandPrice(demand, judicial) {
  const table = normalizeJudicialPricing(judicial);
  const band = (Number(demand.claimed) || 0) <= AMOUNT_THRESHOLD ? 'low' : 'high';
  const fee = PROCEDURE_KIND_VALUES.includes(demand.kind) ? table?.[`${demand.kind}_${band}`] : null;
  // La tasa, por tipo, región y el mismo tramo de importe que el precio. Sin
  // tipo no se sabe cuál: solo la real, si se ha puesto.
  const regionFee =
    demand.region && PROCEDURE_KIND_VALUES.includes(demand.kind)
      ? table?.[courtFeeField(demand.kind, demand.region, band)]
      : null;
  const courtFee = isMoney(demand.court_fee) ? demand.court_fee : regionFee;
  return {
    band,
    fee: isMoney(fee) ? round2(fee) : null,
    court_fee: isMoney(courtFee) ? round2(courtFee) : null,
  };
}

/**
 * La parte judicial del mes: cada demanda con su precio, y los totales.
 * Los honorarios llevan IVA; las tasas no (suplidos).
 */
function judicialFees(demands, judicial) {
  const items = (demands || []).map((d) => ({ ...d, ...demandPrice(d, judicial) }));
  const sum = (k) => round2(items.reduce((s, i) => s + (i[k] || 0), 0));
  return {
    items,
    fees: sum('fee'),
    court_fees: sum('court_fee'),
    // Las que no suman porque falta el tipo de procedimiento o su precio.
    without_price: items.filter((i) => i.fee === null).length,
  };
}

module.exports = {
  PROCEDURE_KINDS,
  PROCEDURE_KIND_VALUES,
  PROCEDURE_KIND_LABELS,
  REGIONS,
  REGION_VALUES,
  REGION_LABELS,
  AMOUNT_THRESHOLD,
  JUDICIAL_PRICE_FIELDS,
  COURT_FEE_FIELDS,
  courtFeeField,
  LEGACY_COURT_FEE_FIELDS,
  ALL_LEGACY_COURT_FEE_FIELDS,
  normalizeJudicialPricing,
  regionFromZip,
  hasJudicialPricing,
  validateJudicialPricing,
  demandPrice,
  judicialFees,
};
